/**
 * AudioPlayerContext — one <audio> element for the whole app.
 *
 * Adapted from memoneo's src/lib/audioPlayer.tsx (structure, Media Session
 * wiring, and per-track localStorage position persistence) — copié de
 * memoneo le 2026-09-19, and heavily simplified: Chouette plays exactly one
 * story at a time (no queue, no server-side bookmark sync, no branding
 * skip-logic), so this version drops everything memoneo needed for those.
 * What's kept: a single element mounted once outside <Routes> (App.tsx) so
 * playback survives navigation, Media Session lock-screen controls, ±15s
 * seek, playback rate, and a resume position remembered per track id.
 *
 * 2026-09-22: also ported the wedged-playback recovery (see "Recovery from
 * an element wedged without erroring" below) — iOS reclaims the decoder of
 * an <audio> element left paused in the background (screen locked), and
 * hands back a MUTE one on the next play: no error, no event, `paused`
 * still flips to false, the position just never moves again. Reported from
 * both this app and memoneo, whose players share no code — the platform is
 * the common factor, not either app's own logic.
 *
 * 2026-09-24: the 09-22 fix was detect-THEN-repair — it worked once the app
 * was back in the foreground, but not for "pause on the lock screen, press
 * play on the lock screen": that handler runs while the phone is still
 * locked, and a backgrounded PWA's JS timers don't run at all while
 * locked, so the 1.8s stall check scheduled from there never fires. iOS
 * itself keeps the lock screen's clock moving regardless (it's
 * extrapolating from "media session says playing", not from real
 * progress), which is why the displayed time was the one thing that did
 * "advance". Two changes close the gap:
 *   - Preventive reload: `pause` now records when it happened and whether
 *     the page was hidden at any point since (that's the condition under
 *     which iOS reclaims the decoder). A resume — lock-screen play button,
 *     or the in-app toggle/play button — checks `shouldReloadBeforeResume`
 *     BEFORE calling `audio.play()`: past the threshold, it reassigns `src`
 *     and calls `play()` synchronously in the same task, because that user
 *     gesture (the tap) is the only user-activation the reload's `play()`
 *     will ever get. This is play-before-you-know-it's-broken, not
 *     detect-then-repair, and it's the only thing that can work while the
 *     screen is still locked.
 *   - `hasProgressed` used to treat ANY change in `buffered`'s end as
 *     progress. That's wrong the moment the decoder gets reclaimed:
 *     `buffered` can shrink or reset along with it, which read as "moved"
 *     and kept resetting the stall baseline — the return-to-foreground
 *     recovery in `recoverIfStalled` would then never fire. Only *growth*
 *     counts now.
 * Also: the Media Session's `playbackState` and `setPositionState` are now
 * kept in sync with real `play`/`pause`/`loadedmetadata`/`seeked` events,
 * so the lock screen stops extrapolating a moving clock over silence in
 * the first place. And a small always-on diagnostics ring buffer
 * (src/lib/playerLog.ts, readable from Settings) records what actually
 * happened on-device, since none of this reproduces with a debugger
 * attached (attaching one requires the screen to stay unlocked).
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from "react";
import type { StoryChapter, StoryKind } from "./types";
import {
  deadlineForMode, endOfCurrentChapterS, shouldPauseNow, type SleepTimerMode,
} from "./sleepTimer";
import { logPlayerEvent } from "./playerLog";
import { push as pushListenEvent } from "./listenQueue";
import { currentSceneChapter, hasAnyChapterImage, nextSceneChapter, toSceneChapters } from "./chapterScenes";
import * as mediaSession from "../native/mediaSession";

export interface AudioTrack {
  /** Story id — the localStorage/Media-Session dedup key. */
  id: string;
  url: string;
  title: string;
  subtitle?: string;
  artwork?: string;
  coverSvg?: string | null;
  /** `cover_image_url` / `cover_image_thumb_url` of the story — the
   *  approved illustration, drawn over `coverSvg` when set. */
  coverImageUrl?: string | null;
  coverImageThumbUrl?: string | null;
  /** `cover_colorway` of the story this track came from. */
  coverColorway?: string | null;
  chapters: StoryChapter[];
  /** `story.kind` — set by every caller of `play()`. Drives the share
   *  button (see src/lib/share.ts): only a "catalog" story's link is safe
   *  to hand out, a "custom" one is written for one named child. */
  kind?: StoryKind;
}

const POSITION_KEY_PREFIX = "chouette.audio.position.";

function readSavedPosition(trackId: string): number {
  try {
    const raw = localStorage.getItem(POSITION_KEY_PREFIX + trackId);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function savePosition(trackId: string, seconds: number) {
  try {
    localStorage.setItem(POSITION_KEY_PREFIX + trackId, String(Math.floor(seconds)));
  } catch {
    /* best effort — private browsing etc. */
  }
}

interface PlayerState {
  track: AudioTrack | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  playbackRate: number;
  sleepTimerMode: SleepTimerMode;
  sleepDeadlineMs: number | null;
}

interface PlayerApi extends PlayerState {
  play: (track: AudioTrack, opts?: { resume?: boolean }) => void;
  toggle: () => void;
  close: () => void;
  seekTo: (seconds: number) => void;
  seekBy: (deltaSeconds: number) => void;
  setPlaybackRate: (rate: number) => void;
  setSleepTimerMode: (mode: SleepTimerMode) => void;
  currentChapter: StoryChapter | null;
  /** Full-size (1024px) illustration of the chapter currently playing —
   *  `null` before the current chapter's own image (or the story has none
   *  for this stretch), in which case the caller falls back to the story
   *  cover. See `lib/chapterScenes.ts`. */
  sceneImageUrl: string | null;
  /** Same chapter's 400px thumb, for a smaller context than the full one
   *  above (unused by the full-screen player today, kept for parity with
   *  `Cover`'s own thumb/full split). */
  sceneImageThumbUrl: string | null;
  /** The next chapter's full-size image to warm up ahead of the crossfade,
   *  or `null` when there is none left to preload. */
  nextSceneImageUrl: string | null;
  /** Whether this story has at least one chapter illustration anywhere —
   *  the Listen/kid-mode pages use this to decide whether the "Images de
   *  l'histoire" toggle appears at all. */
  hasChapterScenes: boolean;
}

const AudioPlayerCtx = createContext<PlayerApi | null>(null);

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [state, setState] = useState<PlayerState>({
    track: null,
    isPlaying: false,
    currentTime: 0,
    duration: 0,
    playbackRate: 1,
    sleepTimerMode: "off",
    sleepDeadlineMs: null,
  });
  // Read from callbacks/timers that must never go stale (reloadAndResume,
  // the Media Session handlers below) without re-subscribing on every
  // render — same trick memoneo's player uses for the same reason.
  const trackRef = useRef<AudioTrack | null>(null);
  trackRef.current = state.track;

  // ── Wedged-playback recovery bookkeeping — see the section near the
  // bottom of this file for what these drive and why. ──────────────────
  //
  // Does the listener currently want sound? Set by every gesture that
  // starts playback, cleared by every explicit pause. Recovery only ever
  // acts inside that window — an element nobody is listening to has
  // nothing to recover *to*.
  const wantsPlaybackRef = useRef(false);
  // Last position playback actually reached — where a recovery reload has
  // to land. `audio.currentTime` isn't it: a just-reloaded element can
  // briefly report 0 before the seek lands.
  const lastPositionRef = useRef(0);
  // Last reading of "is this element actually moving" — position AND
  // buffered end, timestamped. Sentinel -1 keeps the very first sample
  // from ever looking like a stall (see hasProgressed's doc).
  const progressSampleRef = useRef<PlaybackProgress & { atMs: number }>(
    { positionSec: -1, bufferedEndSec: -1, atMs: 0 },
  );
  // Rate-limits auto-recovery so a source that keeps dying ends up stuck
  // instead of reloading forever (0 = never recovered yet).
  const lastRecoveryAtRef = useRef(0);
  // Pending "did the lock-screen play actually produce sound?" check — see
  // the Media Session play handler. Held so a second press, or a track
  // change, doesn't leave an orphan timer behind.
  const wedgeCheckRef = useRef<number | null>(null);
  // When the element was last paused (null while playing) — the clock the
  // preventive-reload threshold below measures against. Set on the `pause`
  // event, cleared on `play`, so it tracks the real element regardless of
  // which code path paused it.
  const pausedAtMsRef = useRef<number | null>(null);
  // Was the document hidden at any point *during* the current pause? Set on
  // `pause` from the current visibility, then OR'd in by the visibility
  // listener below for as long as the element stays paused — covers both
  // "paused already on the lock screen" and "paused in-app, locked later".
  // This, not `paused` alone, is the condition under which iOS reclaims the
  // decoder: a long pause with the tab kept in the foreground never loses
  // it.
  const hiddenSincePauseRef = useRef(false);

  // ── Listen tracking (plan Lot C.2) — which track id "start"/"complete"
  // was last queued for. Reset on an actual track change (a resume of the
  // same track must not re-send either), read/written only from the native
  // `play`/`timeupdate`/`ended` handlers below so it always reflects
  // playback that actually happened, not just an API call that requested it.
  const listenStartSentForRef = useRef<string | null>(null);
  const listenCompleteSentForRef = useRef<string | null>(null);

  /** Re-arm the element after it's found (or is expected to be) wedged, and
   *  resume at `atSec`. Re-assigning `src` (not just calling `load()`) is
   *  what actually resets the element's internal decoder state on iOS —
   *  `load()` alone can leave the reclaimed one in place. `load()` also
   *  resets `playbackRate` to its default, so the listener's chosen speed
   *  is carried across explicitly rather than silently dropping back to 1×.
   *
   *  `immediate: true` additionally calls `play()` synchronously, in the
   *  same task as this call — required for the *preventive* reload
   *  (`shouldReloadBeforeResume`), which runs directly inside a lock-screen
   *  or in-app tap handler. That tap is the only user-activation the new
   *  `play()` will ever get: by the time `loadedmetadata` fires, the
   *  gesture's task is long gone, and a `play()` called from there can be
   *  silently refused. Setting `currentTime` before metadata is loaded is
   *  usually a no-op, which is why it's re-applied in `onLoaded` regardless
   *  of `immediate`. */
  const reloadAndResume = useCallback((atSec: number, autoplay: boolean, opts?: { immediate?: boolean }) => {
    const audio = audioRef.current;
    const src = trackRef.current?.url;
    if (!audio || !src) return;
    const chosenRate = audio.playbackRate;
    const onLoaded = () => {
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.playbackRate = chosenRate;
      if (atSec > 0 && Number.isFinite(audio.duration) && atSec < audio.duration) {
        audio.currentTime = atSec;
      }
      setMediaSessionPositionState(audio);
      if (autoplay && !opts?.immediate) audio.play().catch(() => {});
    };
    audio.addEventListener("loadedmetadata", onLoaded);
    audio.src = src;
    audio.load();
    if (opts?.immediate) {
      if (atSec > 0) audio.currentTime = atSec;
      audio.playbackRate = chosenRate;
      if (autoplay) audio.play().catch(() => {});
    }
  }, []);

  /** Snapshot where playback is right now, as the baseline the stall check
   *  below measures against. Called whenever something asks the element to
   *  play without being able to tell whether it really will. */
  const markProgress = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    progressSampleRef.current = { ...readProgress(audio), atMs: Date.now() };
  }, []);

  /** Reload the element if it claims to be playing but has stopped moving.
   *  Returns whether it did.
   *
   *  Samples FIRST: an element that has advanced since the last reading
   *  just refreshes the baseline and returns false. That ordering is what
   *  makes this safe to call on a baseline of unknown age — coming back
   *  from the background, where no timer of ours ran at all — without
   *  disturbing a playback that's actually fine. */
  const recoverIfStalled = useCallback((timeoutMs: number) => {
    const audio = audioRef.current;
    if (!audio) return false;
    const now = Date.now();
    const sample = readProgress(audio);
    const previous = progressSampleRef.current;
    if (hasProgressed(previous, sample)) {
      progressSampleRef.current = { ...sample, atMs: now };
      return false;
    }
    if (!isPlaybackWedged({
      wantsPlayback: wantsPlaybackRef.current,
      paused: audio.paused,
      msSinceProgress: now - previous.atMs,
      timeoutMs,
    })) return false;
    if (!shouldAutoRecover(wantsPlaybackRef.current, now - lastRecoveryAtRef.current)) {
      logPlayerEvent("recovery-skipped-cooldown", { timeoutMs, ...snapshotAudio(audio) });
      return false;
    }
    lastRecoveryAtRef.current = now;
    progressSampleRef.current = { positionSec: -1, bufferedEndSec: -1, atMs: now };
    logPlayerEvent("recovery-reload", { timeoutMs, ...snapshotAudio(audio) });
    reloadAndResume(lastPositionRef.current, true);
    return true;
  }, [reloadAndResume]);

  const play = useCallback((track: AudioTrack, opts?: { resume?: boolean }) => {
    const audio = audioRef.current;
    if (!audio) return;
    wantsPlaybackRef.current = true;
    // A deliberate call is a fresh start: whatever failed before gets a new
    // automatic recovery attempt, cooldown or not.
    lastRecoveryAtRef.current = 0;
    const isSameTrack = state.track?.id === track.id;
    if (!isSameTrack) {
      lastPositionRef.current = 0;
      progressSampleRef.current = { positionSec: -1, bufferedEndSec: -1, atMs: 0 };
      pausedAtMsRef.current = null;
      hiddenSincePauseRef.current = false;
      listenStartSentForRef.current = null;
      listenCompleteSentForRef.current = null;
      audio.src = track.url;
      const resumeAt = opts?.resume !== false ? readSavedPosition(track.id) : 0;
      audio.currentTime = resumeAt;
      setState((s) => ({
        ...s, track, currentTime: resumeAt, duration: 0, sleepTimerMode: "off", sleepDeadlineMs: null,
      }));
      audio.play().catch(() => {
        // Autoplay can be refused before a user gesture reaches the
        // element; the play/pause button reflects `isPlaying` from the
        // real `play` event below, not an optimistic guess, so the UI
        // won't lie.
      });
      return;
    }
    // Same track: this is a resume, same preventive check as toggle()'s
    // resume path below — covers "Play" tapped from a card/route rather
    // than the mini player, after the track was paused long enough, with
    // the app hidden at some point, for iOS to have reclaimed the decoder.
    const pausedForMs = pausedAtMsRef.current == null ? 0 : Date.now() - pausedAtMsRef.current;
    const hiddenDuringPause = hiddenSincePauseRef.current;
    if (shouldReloadBeforeResume({ pausedForMs, hiddenDuringPause, thresholdMs: PREVENTIVE_RELOAD_THRESHOLD_MS })) {
      logPlayerEvent("preventive-reload", { source: "play", pausedForMs, hiddenDuringPause });
      progressSampleRef.current = { positionSec: -1, bufferedEndSec: -1, atMs: Date.now() };
      reloadAndResume(lastPositionRef.current, true, { immediate: true });
      return;
    }
    audio.play().catch(() => {});
  }, [state.track, reloadAndResume]);

  const toggle = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !state.track) return;
    if (audio.paused) {
      wantsPlaybackRef.current = true;
      lastRecoveryAtRef.current = 0;
      const pausedForMs = pausedAtMsRef.current == null ? 0 : Date.now() - pausedAtMsRef.current;
      const hiddenDuringPause = hiddenSincePauseRef.current;
      if (shouldReloadBeforeResume({ pausedForMs, hiddenDuringPause, thresholdMs: PREVENTIVE_RELOAD_THRESHOLD_MS })) {
        // Same reasoning as the Media Session play handler: don't even try
        // `audio.play()` on what's very likely a stale element — reload
        // and play the fresh one instead, synchronously, in this tap's own
        // task (the only user-activation it gets).
        logPlayerEvent("preventive-reload", { source: "toggle", pausedForMs, hiddenDuringPause });
        progressSampleRef.current = { positionSec: -1, bufferedEndSec: -1, atMs: Date.now() };
        reloadAndResume(lastPositionRef.current, true, { immediate: true });
        return;
      }
      audio.play().catch(() => {});
    } else {
      // A tap while the element claims to be playing normally means
      // "pause" — but a wedged element claims exactly that too, and
      // pausing it is what used to make this take two taps to clear (pause
      // a silent element, then play it again). Repair first; the cooldown
      // is reset because a deliberate tap is always a fresh start.
      lastRecoveryAtRef.current = 0;
      if (recoverIfStalled(STALL_TIMEOUT_MS)) return;
      wantsPlaybackRef.current = false;
      audio.pause();
    }
  }, [state.track, recoverIfStalled, reloadAndResume]);

  const close = useCallback(() => {
    const audio = audioRef.current;
    // Nobody wants sound from a closed player — without this, a stray
    // watchdog/visibilitychange tick still in flight could otherwise read
    // the stale `true` and try to "recover" an element nothing is playing.
    wantsPlaybackRef.current = false;
    lastRecoveryAtRef.current = 0;
    lastPositionRef.current = 0;
    pausedAtMsRef.current = null;
    hiddenSincePauseRef.current = false;
    if (audio) {
      audio.pause();
      // Detach the source without loading anything: `audio.src = ""` resolves
      // against the current document URL and makes the browser re-fetch the
      // page itself, not just clear playback.
      audio.removeAttribute("src");
      audio.load();
    }
    // The resume position in localStorage is intentionally left untouched —
    // closing the mini player isn't "finishing" the story, just dismissing
    // the bar, so the next `play()` picks up where this one left off.
    setState((s) => ({
      ...s, track: null, isPlaying: false, currentTime: 0, duration: 0, sleepTimerMode: "off", sleepDeadlineMs: null,
    }));
  }, []);

  const seekTo = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Math.max(0, Math.min(seconds, audio.duration || seconds));
  }, []);

  const seekBy = useCallback((delta: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    seekTo(audio.currentTime + delta);
  }, [seekTo]);

  const setPlaybackRate = useCallback((rate: number) => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = rate;
      setMediaSessionPositionState(audio);
    }
    setState((s) => ({ ...s, playbackRate: rate }));
  }, []);

  /** Pause playback for the sleep timer and reset its mode back to "off".
   *  Also marks the listener as no longer wanting playback — same as an
   *  explicit pause — so the wedged-playback recovery above (driven by
   *  `wantsPlaybackRef`) never mistakes a timer-triggered pause for a wedge
   *  and tries to "recover" it. */
  const pauseForSleepTimer = useCallback(() => {
    audioRef.current?.pause();
    wantsPlaybackRef.current = false;
    setState((s) => ({ ...s, sleepTimerMode: "off", sleepDeadlineMs: null }));
  }, []);

  const setSleepTimerMode = useCallback((mode: SleepTimerMode) => {
    setState((s) => ({
      ...s,
      sleepTimerMode: mode,
      sleepDeadlineMs: deadlineForMode(mode, Date.now()),
    }));
  }, []);

  // Wire the single <audio> element's events to state, once.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlay = () => {
      pausedAtMsRef.current = null;
      setMediaSessionPlaybackState("playing");
      setMediaSessionPositionState(audio);
      logPlayerEvent("play", snapshotAudio(audio));
      // "start", once per loaded track — a resume after pause fires this
      // same native event again, which is exactly what the ref guards
      // against (it's only cleared on an actual track change, above).
      const track = trackRef.current;
      if (track && listenStartSentForRef.current !== track.id) {
        listenStartSentForRef.current = track.id;
        pushListenEvent({ story_id: track.id, event: "start", at: new Date().toISOString() });
      }
      setState((s) => ({ ...s, isPlaying: true }));
    };
    const onPause = () => {
      pausedAtMsRef.current = Date.now();
      hiddenSincePauseRef.current = document.visibilityState === "hidden";
      setMediaSessionPlaybackState("paused");
      setMediaSessionPositionState(audio);
      logPlayerEvent("pause", snapshotAudio(audio));
      setState((s) => ({ ...s, isPlaying: false }));
    };
    const onLoadedMetadata = () => {
      setMediaSessionPositionState(audio);
      logPlayerEvent("loadedmetadata", snapshotAudio(audio));
      setState((s) => ({ ...s, duration: audio.duration || 0 }));
    };
    const onSeeked = () => setMediaSessionPositionState(audio);
    const onStalled = () => logPlayerEvent("stalled", snapshotAudio(audio));
    const onWaiting = () => logPlayerEvent("waiting", snapshotAudio(audio));
    const onError = () => logPlayerEvent("error", { ...snapshotAudio(audio), errorCode: audio.error?.code ?? null });
    const onTimeUpdate = () => {
      setState((s) => ({ ...s, currentTime: audio.currentTime }));
      // Only positive times: a reload passes through 0 before the seek
      // lands, and overwriting the recovery target with that would send a
      // second failed recovery back to the start of the story.
      if (audio.currentTime > 0) lastPositionRef.current = audio.currentTime;
      if (state.track) savePosition(state.track.id, audio.currentTime);
      // "complete" at 90% listened — once per loaded track. `ended` (below)
      // covers the story that plays all the way to its very last second
      // without a `timeupdate` tick ever landing past the threshold.
      const track = trackRef.current;
      if (
        track && listenCompleteSentForRef.current !== track.id
        && audio.duration > 0 && audio.currentTime / audio.duration >= 0.9
      ) {
        listenCompleteSentForRef.current = track.id;
        pushListenEvent({ story_id: track.id, event: "complete", at: new Date().toISOString() });
      }
    };
    const onEnded = () => {
      logPlayerEvent("ended", snapshotAudio(audio));
      // A finished story is not one the listener still wants playing —
      // otherwise the return-to-foreground recovery would restart its
      // last second every time the app is reopened.
      wantsPlaybackRef.current = false;
      if (state.track) savePosition(state.track.id, 0);
      const track = trackRef.current;
      if (track && listenCompleteSentForRef.current !== track.id) {
        listenCompleteSentForRef.current = track.id;
        pushListenEvent({ story_id: track.id, event: "complete", at: new Date().toISOString() });
      }
      setState((s) => ({ ...s, isPlaying: false, currentTime: 0 }));
    };
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("seeked", onSeeked);
    audio.addEventListener("stalled", onStalled);
    audio.addEventListener("waiting", onWaiting);
    audio.addEventListener("error", onError);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("seeked", onSeeked);
      audio.removeEventListener("stalled", onStalled);
      audio.removeEventListener("waiting", onWaiting);
      audio.removeEventListener("error", onError);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("ended", onEnded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.track?.id]);

  // Wedged-playback watchdog. Nothing about a wedged element fires an
  // event, so the only way to notice it is to watch the position. Runs
  // only while the player believes it is playing, and costs one
  // `currentTime` read per second — a healthy element just keeps
  // refreshing its own baseline inside `recoverIfStalled`.
  useEffect(() => {
    if (!state.isPlaying) return;
    markProgress();
    const id = window.setInterval(() => { recoverIfStalled(STALL_TIMEOUT_MS); }, STALL_POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [state.isPlaying, markProgress, recoverIfStalled]);

  // Coming back into the app must never land on a Pause button over a
  // frozen scrub bar — the state a failed lock-screen play leaves behind.
  // The interval above is no help here: a backgrounded tab's timers are
  // throttled to a crawl (or not run at all), which is exactly why the
  // check repeats on the way back in, with the short timeout (see
  // LOCKSCREEN_STALL_TIMEOUT_MS). An element that really did keep playing
  // in the background reports a moved position and is left alone.
  //
  // Also doubles as the `hiddenSincePauseRef` tracker: while the element is
  // paused, every transition to "hidden" latches that flag for as long as
  // the pause lasts (see the ref's own comment) — this is the only place
  // that can see "paused, then hidden" as distinct from "paused, still
  // visible", since the `pause` event alone only knows the instant it
  // fired.
  useEffect(() => {
    const onVisibilityChange = () => {
      const audio = audioRef.current;
      logPlayerEvent("visibilitychange", {
        visibilityState: document.visibilityState,
        ...(audio ? snapshotAudio(audio) : {}),
      });
      if (document.visibilityState !== "visible") {
        if (audio?.paused) hiddenSincePauseRef.current = true;
        return;
      }
      if (!wantsPlaybackRef.current || !audio) return;
      if (audio.paused) {
        // The element is paused but the listener still wants sound — a
        // lock-screen play that never truly resumed, or a resume that got
        // wedged before the watchdog could catch it. Re-arm the element but
        // do NOT autoplay: a pause the system imposed (headphones pulled,
        // a call) must not turn into sound blasting out of the speaker in
        // a sleeping child's room the moment the parent unlocks. The UI
        // already shows Paused (`isPlaying` follows the real events), and
        // one tap on Play resumes on a fresh element.
        logPlayerEvent("recovery-reload", { source: "visible-while-paused", ...snapshotAudio(audio) });
        lastRecoveryAtRef.current = Date.now();
        progressSampleRef.current = { positionSec: -1, bufferedEndSec: -1, atMs: Date.now() };
        reloadAndResume(lastPositionRef.current, false);
        return;
      }
      recoverIfStalled(LOCKSCREEN_STALL_TIMEOUT_MS);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [recoverIfStalled, reloadAndResume]);

  const currentChapter = useMemo<StoryChapter | null>(() => {
    if (!state.track || state.track.chapters.length === 0) return null;
    const sorted = [...state.track.chapters].sort((a, b) => a.start_s - b.start_s);
    let found: StoryChapter | null = null;
    for (const c of sorted) {
      if (c.start_s <= state.currentTime) found = c;
    }
    return found ?? sorted[0];
  }, [state.track, state.currentTime]);

  // Chapter scenes (lot: chapter scenes player) — `chapterScenes.ts` is the
  // pure decision, this is just adapting the track's chapters into its
  // input shape and reading the result back out for the pages and the
  // Media Session effect below.
  const sceneChapters = useMemo(
    () => toSceneChapters(state.track?.chapters ?? []),
    [state.track],
  );
  const currentScene = useMemo(
    () => currentSceneChapter(sceneChapters, state.currentTime),
    [sceneChapters, state.currentTime],
  );
  const nextScene = useMemo(
    () => nextSceneChapter(sceneChapters, state.currentTime),
    [sceneChapters, state.currentTime],
  );
  const hasChapterScenes = useMemo(() => hasAnyChapterImage(sceneChapters), [sceneChapters]);

  // Sleep timer enforcement: re-evaluated on every currentTime tick.
  useEffect(() => {
    if (state.sleepTimerMode === "off" || !state.track) return;
    const chapterBoundaryS =
      state.sleepTimerMode === "end_of_chapter"
        ? endOfCurrentChapterS(
            state.track.chapters.map((c) => ({ startS: c.start_s })),
            state.currentTime,
          )
        : null;
    const pause = shouldPauseNow({
      mode: state.sleepTimerMode,
      nowMs: Date.now(),
      deadlineMs: state.sleepDeadlineMs,
      positionS: state.currentTime,
      chapterBoundaryS,
    });
    if (pause) pauseForSleepTimer();
  }, [state.currentTime, state.sleepTimerMode, state.sleepDeadlineMs, state.track, pauseForSleepTimer]);

  // Sleep timer safety net: the effect above only re-evaluates on
  // `timeupdate`, which a locked iOS PWA can throttle to a crawl (or stop
  // firing altogether) — a minute-based timer could then run well past its
  // deadline before anything notices. Two independent paths cover that:
  //  - `visibilitychange` re-checks against `audio.currentTime` (still
  //    readable even when its update *events* are throttled) as soon as the
  //    tab is foregrounded again — catches a deadline that passed while
  //    hidden.
  //  - a `window.setTimeout` armed to the exact deadline covers the case
  //    where the tab never comes back to the foreground during the story at
  //    all (screen just left off). Re-armed whenever the deadline changes,
  //    and cleared on mode change, `close()`, or track change since all of
  //    those also change `state.sleepDeadlineMs`/`state.track`.
  useEffect(() => {
    if (state.sleepTimerMode === "off" || !state.track) return;
    const track = state.track;
    const check = () => {
      const audio = audioRef.current;
      // No visibility guard: if the deadline timeout does get to run while
      // the screen is locked, that is exactly when it must pause.
      if (!audio) return;
      const chapterBoundaryS =
        state.sleepTimerMode === "end_of_chapter"
          ? endOfCurrentChapterS(track.chapters.map((c) => ({ startS: c.start_s })), audio.currentTime)
          : null;
      const pause = shouldPauseNow({
        mode: state.sleepTimerMode,
        nowMs: Date.now(),
        deadlineMs: state.sleepDeadlineMs,
        positionS: audio.currentTime,
        chapterBoundaryS,
      });
      if (pause) pauseForSleepTimer();
    };
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    let timeoutId: number | null = null;
    if (state.sleepDeadlineMs !== null) {
      timeoutId = window.setTimeout(check, Math.max(0, state.sleepDeadlineMs - Date.now()));
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [state.sleepTimerMode, state.sleepDeadlineMs, state.track, pauseForSleepTimer]);

  // Media Session metadata — title/artist/artwork. Split from the action
  // handlers below on purpose: this one also re-runs when the displayed
  // chapter scene changes (`currentScene`), and re-registering play/pause/
  // seek handlers on every chapter boundary would be pure churn.
  useEffect(() => {
    if (!mediaSession.available() || !state.track) return;
    const sceneUrl = currentScene?.imageUrl || currentScene?.imageThumbUrl || null;
    const artwork = sceneUrl
      ? [{ src: sceneUrl, sizes: "1024x1536", type: "image/webp" }]
      : state.track.artwork
        ? [{
            src: state.track.artwork,
            sizes: "512x512",
            type: state.track.artwork.startsWith("data:image/svg+xml") ? "image/svg+xml" : "image/png",
          }]
        : [];
    mediaSession.setMetadata({
      title: state.track.title,
      artist: "Chouette, par memoneo",
      album: state.track.subtitle,
      artwork,
    });
  }, [state.track, currentScene]);

  // Media Session — lock-screen / control-center transport (action
  // handlers only, metadata is the effect right above).
  useEffect(() => {
    if (!mediaSession.available() || !state.track) return;
    mediaSession.setActionHandler("play", () => {
      wantsPlaybackRef.current = true;
      lastRecoveryAtRef.current = 0;
      const audio = audioRef.current;
      if (!audio) return;
      const pausedForMs = pausedAtMsRef.current == null ? 0 : Date.now() - pausedAtMsRef.current;
      const hiddenDuringPause = hiddenSincePauseRef.current;
      logPlayerEvent("mediaSession:play", { pausedForMs, hiddenDuringPause, ...snapshotAudio(audio) });
      if (shouldReloadBeforeResume({ pausedForMs, hiddenDuringPause, thresholdMs: PREVENTIVE_RELOAD_THRESHOLD_MS })) {
        // This handler IS the lock-screen play button, running while the
        // phone may still be locked — the only user-activation the reload
        // will ever get, and the only moment any of our JS runs at all
        // until the listener unlocks the phone. Detect-then-repair (the
        // watchdog below) can't help here: its timers don't run in a
        // locked, backgrounded PWA. So when the pause was long enough,
        // with the app hidden at some point during it, for iOS to have
        // very likely reclaimed the decoder already, skip straight to
        // reload-and-resume instead of calling `play()` on a stale
        // element — synchronously, same task, `immediate: true`.
        logPlayerEvent("preventive-reload", { source: "mediaSession", pausedForMs, hiddenDuringPause });
        progressSampleRef.current = { positionSec: -1, bufferedEndSec: -1, atMs: Date.now() };
        reloadAndResume(lastPositionRef.current, true, { immediate: true });
        return;
      }
      markProgress();
      audio.play().catch(() => {});
      // Fallback for the shorter-pause case: still theoretically possible
      // for iOS to have reclaimed the decoder despite the pause being
      // under the preventive threshold. `play` resolves, the `play` event
      // fires, `paused` goes false, and no sound ever comes out — nothing
      // else would catch it in time here (the watchdog's interval barely
      // runs in a backgrounded tab), so check once, right after. A timer
      // that fires late (likely, backgrounded) only makes the verdict
      // safer: the gap it measures has grown.
      if (wedgeCheckRef.current != null) window.clearTimeout(wedgeCheckRef.current);
      wedgeCheckRef.current = window.setTimeout(() => {
        wedgeCheckRef.current = null;
        recoverIfStalled(LOCKSCREEN_STALL_TIMEOUT_MS);
      }, LOCKSCREEN_STALL_CHECK_DELAY_MS);
    });
    mediaSession.setActionHandler("pause", () => {
      wantsPlaybackRef.current = false;
      logPlayerEvent("mediaSession:pause");
      audioRef.current?.pause();
    });
    mediaSession.setActionHandler("seekbackward", () => seekBy(-15));
    mediaSession.setActionHandler("seekforward", () => seekBy(15));
    return () => {
      if (wedgeCheckRef.current != null) {
        window.clearTimeout(wedgeCheckRef.current);
        wedgeCheckRef.current = null;
      }
      mediaSession.setActionHandler("play", null);
      mediaSession.setActionHandler("pause", null);
      mediaSession.setActionHandler("seekbackward", null);
      mediaSession.setActionHandler("seekforward", null);
    };
  }, [state.track, seekBy, markProgress, recoverIfStalled, reloadAndResume]);

  const api = useMemo<PlayerApi>(() => ({
    ...state,
    play,
    toggle,
    close,
    seekTo,
    seekBy,
    setPlaybackRate,
    setSleepTimerMode,
    currentChapter,
    sceneImageUrl: currentScene?.imageUrl ?? null,
    sceneImageThumbUrl: currentScene?.imageThumbUrl ?? null,
    nextSceneImageUrl: nextScene?.imageUrl ?? null,
    hasChapterScenes,
  }), [
    state, play, toggle, close, seekTo, seekBy, setPlaybackRate, setSleepTimerMode, currentChapter,
    currentScene, nextScene, hasChapterScenes,
  ]);

  return (
    <AudioPlayerCtx.Provider value={api}>
      {children}
      {/* Single global element, outside <Routes> — see the header comment. */}
      <audio ref={audioRef} preload="metadata" />
    </AudioPlayerCtx.Provider>
  );
}

export function useAudioPlayer(): PlayerApi {
  const ctx = useContext(AudioPlayerCtx);
  if (!ctx) throw new Error("useAudioPlayer must be used inside AudioPlayerProvider");
  return ctx;
}

// ── Recovery from an element wedged without erroring ───────────────────
//
// iOS reclaims the decoder of a media element left paused in the background
// — screen locked, app minimised. What comes back is not an *errored*
// element: `error` stays null (this player never even listens for it — it
// has no fatal-error recovery path to begin with), `play()` resolves, the
// `play` event fires, `isPlaying` goes true — and the position never moves.
// The player then shows a Pause button over a frozen scrub bar with no
// sound, and the next tap reads as "pause" because the element does claim
// to be playing, which is why clearing this by hand used to take two taps.
//
// Detection has to be positional: no event announces this state.

/** How long an element that claims to be playing may sit at the same
 *  position — and the same buffered end — before it counts as wedged.
 *  Comfortably longer than a normal rebuffer: a false positive costs a
 *  reload of a stream that was about to resume on its own. */
export const STALL_TIMEOUT_MS = 6_000;

/** The same verdict, but for the two moments where a stall is the EXPECTED
 *  failure rather than an unlikely one — right after a lock-screen play,
 *  and on the way back into the foreground. The decoder is already gone in
 *  both, so the element doesn't get the benefit of the doubt for as long. */
export const LOCKSCREEN_STALL_TIMEOUT_MS = 1_500;

/** How soon after a lock-screen play to check whether sound actually
 *  followed. Slightly longer than the timeout it feeds, so the measured gap
 *  clears it on the first tick rather than needing a second one. */
const LOCKSCREEN_STALL_CHECK_DELAY_MS = 1_800;

/** Watchdog period while playing. One `currentTime` read per second. */
const STALL_POLL_INTERVAL_MS = 1_000;

/** Minimum gap between two silent recoveries. Long enough that a source
 *  failing over and over surfaces as a stuck player instead of looping on
 *  load(), short enough that a second unrelated hiccup later in the same
 *  story still self-heals. */
export const RECOVERY_COOLDOWN_MS = 10_000;

/** Where playback is, and how far the buffer reaches. */
export interface PlaybackProgress {
  positionSec: number;
  bufferedEndSec: number;
}

function readProgress(audio: HTMLAudioElement): PlaybackProgress {
  let bufferedEndSec = 0;
  try {
    if (audio.buffered.length > 0) bufferedEndSec = audio.buffered.end(audio.buffered.length - 1);
  } catch {
    /* InvalidStateError on a torn-down element — treat as "no buffer" */
  }
  return { positionSec: audio.currentTime, bufferedEndSec };
}

/** Diagnostics snapshot of an element's playback state, for the player log. */
function snapshotAudio(audio: HTMLAudioElement): Record<string, unknown> {
  return {
    position: Math.round(audio.currentTime * 100) / 100,
    paused: audio.paused,
    bufferedEnd: readProgress(audio).bufferedEndSec,
    readyState: audio.readyState,
    networkState: audio.networkState,
  };
}

function setMediaSessionPlaybackState(playbackState: MediaSessionPlaybackState) {
  mediaSession.setPlaybackState(playbackState);
}

/** Keep the lock screen's own clock honest. Without this, `playbackState`
 *  stays whatever it last was (often stuck on "playing") and the OS
 *  extrapolates a moving position from it — which is what made the
 *  lock-screen time keep advancing over actual silence in the original bug
 *  report, independently of whether audio ever resumed. */
function setMediaSessionPositionState(audio: HTMLAudioElement) {
  if (!mediaSession.available()) return;
  const duration = audio.duration;
  if (!Number.isFinite(duration) || duration <= 0) return;
  const position = Math.min(Math.max(audio.currentTime, 0), duration);
  try {
    mediaSession.setPositionState({ duration, position, playbackRate: audio.playbackRate || 1 });
  } catch {
    // iOS can throw on a torn-down element mid-reload; the next
    // loadedmetadata/play/pause/seeked retries with fresh state.
  }
}

/** Whether two readings of a playing element show it actually moving.
 *
 *  Only buffer GROWTH counts, not just any change: iOS resets or shrinks
 *  `buffered` right along with reclaiming the decoder, so treating that as
 *  "moved" would refresh the stall baseline on the exact reading that
 *  should have tripped it — this is what let a wedged element evade the
 *  return-to-foreground recovery in `recoverIfStalled`. A shrinking or
 *  unchanged buffer is never itself progress; a growing one, even with the
 *  position stuck (a slow connection rebuffering), still is. */
export function hasProgressed(previous: PlaybackProgress, next: PlaybackProgress): boolean {
  return next.positionSec !== previous.positionSec
    || next.bufferedEndSec > previous.bufferedEndSec;
}

/** Whether a silently stalled element should be reloaded.
 *
 *  `paused` is what separates this from an ordinary pause: a listener who
 *  stopped playback on purpose also stops making progress, and must be left
 *  exactly where they are. `wantsPlayback` is the same guard
 *  `shouldAutoRecover` uses — an element nobody is listening to has nothing
 *  to recover to. */
export function isPlaybackWedged(args: {
  wantsPlayback: boolean;
  paused: boolean;
  msSinceProgress: number;
  timeoutMs: number;
}): boolean {
  return args.wantsPlayback && !args.paused && args.msSinceProgress >= args.timeoutMs;
}

/** Whether a wedged element should be answered with one silent
 *  reload-and-resume.
 *
 *  Only while the listener actually wants sound: an element stuck with
 *  nobody listening has nothing to recover *to*, and reloading it would
 *  fetch several MB for no one. */
export function shouldAutoRecover(wantsPlayback: boolean, msSinceLastRecovery: number): boolean {
  return wantsPlayback && msSinceLastRecovery >= RECOVERY_COOLDOWN_MS;
}

// ── Preventive reload before resume ─────────────────────────────────────
//
// The recovery above is detect-THEN-repair: it needs a running timer to
// notice the stall, and a backgrounded, locked PWA's timers don't run.
// That's fine on the way back into the foreground (visibilitychange fires
// the instant the app becomes visible again, timers or not) but not for
// "pause on the lock screen, press play on the lock screen" — that handler
// runs while the phone is still locked, and nothing schedules ever fires
// from there. The only fix is to not wait for a failure at all: decide,
// from the pause alone, whether the decoder was *likely* already reclaimed,
// and if so reload before ever calling `play()` on the stale element.

/** How long a pause has to last, with the page hidden at some point during
 *  it, before a resume should reload first rather than trust the existing
 *  element. Below this, a normal short pause (glancing at the lock screen,
 *  a quick app-switch) is left alone — reloading it would be a needless
 *  network refetch and a small audible gap for the vast majority of
 *  resumes that were never actually wedged. */
export const PREVENTIVE_RELOAD_THRESHOLD_MS = 10_000;

/** Whether a resume should reload-and-resume instead of just calling
 *  `play()`.
 *
 *  `hiddenDuringPause` is the actual condition under which iOS reclaims the
 *  decoder — a long pause with the tab kept in the foreground the whole
 *  time never loses it, only one where the app was backgrounded/locked at
 *  some point does. `pausedForMs` alone would false-positive on "paused for
 *  a while, but stayed on screen the whole time" (e.g. reading a chapter
 *  list before hitting play again). */
export function shouldReloadBeforeResume(args: {
  pausedForMs: number;
  hiddenDuringPause: boolean;
  thresholdMs: number;
}): boolean {
  return args.hiddenDuringPause && args.pausedForMs >= args.thresholdMs;
}
